/**
 * RackPassportCard — Device passport panel shown in the left inspector.
 * SRP: displays selected equipment details, edit/delete/navigate actions.
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trash2, LayoutGrid, X } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { useConfirmStore } from '../../store/confirmStore';
import { api } from '../../utils/api';
import { PatchPanelMapping } from './PatchPanelMapping';
import { DeviceFaceplateViewer } from './DeviceFaceplateViewer';

interface Equipment {
  id: string | number;
  name: string;
  equipment_type: string;
  status?: string;
  u_position?: number;
  u_height?: number;
  mounting_position?: string;
  asset_tag?: string;
  serial_number?: string;
  weight_kg?: number;
  power_w?: number;
  airflow?: string;
  depth?: string;
  [key: string]: any;
}

interface RackPassportCardProps {
  selectedEq: Equipment;
  theme: 'dark' | 'light';
  actualCode: string;
  actualRackId: string | number | undefined;
  onBack: () => void;
  onEdit: (eq: Equipment) => void;
}

/**
 * Shows full equipment passport with attribute grid, actions, and patch panel mapping.
 */
export function RackPassportCard({
  selectedEq,
  theme,
  actualCode,
  actualRackId,
  onBack,
  onEdit,
}: RackPassportCardProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isDark = theme === 'dark';
  const [showFaceplateModal, setShowFaceplateModal] = useState(false);

  useEffect(() => {
    if (!showFaceplateModal) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setShowFaceplateModal(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showFaceplateModal]);

  const { data: portsData = [], isLoading: isPortsLoading } = useQuery<any[]>({
    queryKey: ['equipmentPorts', selectedEq?.id],
    queryFn: () => api.get(`/api/equipment/${selectedEq?.id}/ports`).then(r => r.data),
    enabled: showFaceplateModal && !!selectedEq?.id,
  });

  const { data: cablesData = [], isLoading: isCablesLoading } = useQuery<any[]>({
    queryKey: ['equipmentCables', selectedEq?.id],
    queryFn: () => api.get(`/api/equipment/${selectedEq?.id}/cables`).then(r => r.data).catch(() => []),
    enabled: showFaceplateModal && !!selectedEq?.id,
  });

  const handleDelete = async () => {
    const confirmed = await useConfirmStore.getState().confirm({
      title: 'Удаление оборудования',
      message: `Вы действительно хотите удалить прибор "${selectedEq.name}"?`,
      confirmText: 'Удалить',
      cancelText: 'Отмена'
    });
    if (!confirmed) return;
    try {
      await api.delete(`/api/equipment/${selectedEq.id}`);
      toast.success('Прибор удален');
      onBack();
      queryClient.invalidateQueries({ queryKey: ['rackEquipment', actualCode, actualRackId] });
    } catch {
      toast.error('Не удалось удалить прибор');
    }
  };

  const attrs = [
    { label: 'Позиция',  value: `U${selectedEq.u_position} (${selectedEq.u_height}U)`, accent: 'text-cyan-500' },
    { label: 'Asset Tag', value: selectedEq.asset_tag  || '—', accent: '' },
    { label: 'Serial',    value: selectedEq.serial_number || '—', accent: '' },
    { label: 'Масса',     value: `${selectedEq.weight_kg || 0} кг`,  accent: 'text-emerald-500' },
    { label: 'Мощность',  value: `${selectedEq.power_w  || 0} Вт`,   accent: 'text-amber-500'   },
    { label: 'Обдув',     value: selectedEq.airflow || 'Front-to-Rear', accent: '' },
    { label: 'Глубина',   value: selectedEq.depth   || 'Full Depth',    accent: '' },
  ];

  return (
    <div className="space-y-3">
      {/* Action Header */}
      <div className="flex justify-between items-center bg-slate-950 p-2 rounded-lg border border-slate-800">
        <button
          onClick={onBack}
          className="text-xs text-slate-400 hover:text-cyan-400 flex items-center gap-1 transition"
        >
          ← Обзор стойки
        </button>

        <div className="flex gap-2">
          <button
            onClick={() => setShowFaceplateModal(true)}
            data-testid="btn-open-faceplate"
            className="px-2.5 py-1 bg-slate-800 hover:bg-slate-700 text-cyan-300 rounded font-bold transition border border-cyan-800/60 text-[10px] flex items-center gap-1"
            title="Открыть 2D фасадную панель и порты"
          >
            <LayoutGrid className="w-3 h-3" />
            Фасадная панель
          </button>

          <button
            onClick={() => onEdit(selectedEq)}
            className="px-2.5 py-1 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded font-bold transition border border-slate-700 text-[10px]"
          >
            Редактировать
          </button>

          <button
            onClick={() => {
              if (!actualCode) return;
              navigate(`/${actualCode}/equipment/${selectedEq.id}?fromRack=${actualRackId}`)
            }}
            className="px-2.5 py-1 bg-cyan-600 hover:bg-cyan-500 text-white rounded font-bold transition text-[10px] shadow-md shadow-cyan-500/20"
          >
            Профиль &amp; Порты ➤
          </button>

          <button
            onClick={handleDelete}
            className="p-1 bg-red-950/60 hover:bg-red-900/60 text-red-400 border border-red-800/80 rounded transition"
            title="Удалить прибор"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Passport Card */}
      <div className={`border p-3 rounded-lg relative overflow-hidden ${
        isDark ? 'bg-slate-950 border-slate-800' : 'bg-white border-gray-200'
      }`}>
        {/* Header: type badge + status + name */}
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="px-2 py-0.5 text-[10px] font-bold rounded bg-cyan-950 text-cyan-300 border border-cyan-800 uppercase tracking-wider">
                {selectedEq.equipment_type}
              </span>
              <span className={`px-2 py-0.5 text-[10px] font-bold rounded border uppercase tracking-wider ${
                selectedEq.status === 'Active'  ? 'bg-emerald-950 text-emerald-300 border-emerald-800'
                : selectedEq.status === 'Staged' ? 'bg-cyan-950 text-cyan-300 border-cyan-800'
                : 'bg-red-950 text-red-300 border-red-800'
              }`}>
                ● {selectedEq.status || 'Active'}
              </span>
            </div>
            <h2 className={`text-base font-bold mt-1.5 ${isDark ? 'text-slate-100' : 'text-slate-800'}`}>
              {selectedEq.name}
            </h2>
            {(selectedEq.manufacturer || selectedEq.model) && (
              <p className={`text-[11px] mt-0.5 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                {[selectedEq.manufacturer, selectedEq.model].filter(Boolean).join(' ')}
              </p>
            )}
            <p className={`text-[11px] mt-0.5 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
              ID: {selectedEq.id}
            </p>
          </div>
        </div>

        {/* Attribute cards grid */}
        <div className={`grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3 pt-3 border-t ${
          isDark ? 'border-slate-900' : 'border-gray-200'
        }`}>
          {attrs.map(({ label, value, accent }) => (
            <div key={label} className={`p-2 rounded-lg border ${
              isDark ? 'bg-slate-900/60 border-slate-800' : 'bg-gray-50 border-gray-200'
            }`}>
              <span className={`text-[9px] uppercase block mb-0.5 ${
                isDark ? 'text-slate-500' : 'text-gray-400'
              }`}>{label}</span>
              <span className={`text-xs font-bold block ${
                accent || (isDark ? 'text-slate-200' : 'text-slate-700')
              }`}>{value}</span>
            </div>
          ))}
        </div>

        {/* Patch Panel Front→Rear Mapping */}
        {selectedEq.equipment_type === 'patch_panel' && (
          <div className="bg-slate-950 border border-purple-900/50 p-3 rounded-lg mt-3">
            <PatchPanelMapping
              equipmentId={String(selectedEq.id)}
              equipmentName={selectedEq.name}
            />
          </div>
        )}
      </div>

      {/* Faceplate 2D Viewer Modal */}
      {showFaceplateModal && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="faceplate-modal-title"
          data-testid="modal-faceplate-viewer"
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
          onClick={() => setShowFaceplateModal(false)}
        >
          <div
            className="relative w-full max-w-4xl bg-slate-950 border border-slate-800 rounded-xl shadow-2xl p-6 text-slate-100 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4 pb-2 border-b border-slate-800">
              <div className="flex items-center gap-2">
                <LayoutGrid className="w-5 h-5 text-cyan-400" />
                <h3 id="faceplate-modal-title" className="text-base font-bold text-slate-100">
                  Интерактивная панель оборудования: {selectedEq.name}
                </h3>
              </div>
              <button
                onClick={() => setShowFaceplateModal(false)}
                className="min-h-[44px] min-w-[44px] flex items-center justify-center rounded-lg hover:bg-slate-800 text-slate-400 hover:text-slate-100 transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-cyan-500"
                data-testid="btn-close-faceplate-modal"
                title="Закрыть"
                aria-label="Закрыть модальное окно"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <DeviceFaceplateViewer
              device={{
                ...selectedEq,
                id: String(selectedEq.id),
                u_height: selectedEq.u_height || 1,
                ports: portsData,
              }}
              cables={cablesData}
              isLoading={isPortsLoading || isCablesLoading}
              dcCode={actualCode}
            />
          </div>
        </div>
      )}
    </div>
  );
}
